/**
 * Kiosk gate pages — Idle, Scan, Lookup, Vehicles, Shift.
 *
 * Uses existing gate_pass + visitor endpoints. Camera + QR reader lands with
 * the device firmware — the interim UX accepts a pasted / typed code.
 */
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Alert, Button, Card, Empty, Form, Input, Modal, Popconfirm, Skeleton, Space, Table,
  Tag, Typography, message,
} from 'antd';
import {
  CameraOutlined, CheckCircleOutlined, CloseCircleOutlined, LoginOutlined, LogoutOutlined,
  SearchOutlined, SwapOutlined,
} from '@ant-design/icons';
import type {
  CheckInVisitorRequest, GatePassRow, StudentListQuery,
} from '@velora/contracts';
import { ApiError, productApi } from '../../api';
import { readShift, signInShift, signOutShift, type ShiftState } from './KioskShell';

function extractErrMsg(err: unknown): string | null {
  if (!err) return null;
  if (err instanceof ApiError) return (err.body as { message?: string } | null)?.message ?? err.message;
  return err instanceof Error ? err.message : String(err);
}

/* =========== Idle =========== */

export function KioskIdlePage() {
  const { institution = '' } = useParams<{ institution: string }>();
  const [shift, setShift] = useState<ShiftState | null>(readShift());
  useEffect(() => {
    const onChange = () => setShift(readShift());
    window.addEventListener('velora:gate-shift', onChange);
    window.addEventListener('storage', onChange);
    return () => {
      window.removeEventListener('velora:gate-shift', onChange);
      window.removeEventListener('storage', onChange);
    };
  }, []);
  const passes = useQuery({
    queryKey: ['gate-passes', institution],
    queryFn: () => productApi.listGatePasses(institution),
    enabled: !!institution,
  });
  return (
    <Space direction="vertical" size="large" style={{ width: '100%', textAlign: 'center' }}>
      {!shift && (
        <Alert type="warning" showIcon
          message="No shift signed in"
          description={<>Head to <Link to={`/i/${institution}/gate/shift`} style={{ color: '#7ab6ff' }}>Shift</Link> and sign in before letting anyone through.</>}
          style={{ background: '#2b1a10', borderColor: '#7a3421', color: '#f4a3a0' }}
        />
      )}
      <div style={{ padding: '32px 16px 20px' }}>
        <CameraOutlined style={{ fontSize: 96, color: '#7ab6ff' }} />
      </div>
      <Typography.Title level={2} style={{ color: '#e6ebf3', margin: 0 }}>Ready to scan</Typography.Title>
      <Typography.Text style={{ color: '#8fa3c0' }}>Tap Scan below · or Lookup for a broken QR</Typography.Text>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10, marginTop: 22 }}>
        <Link to={`/i/${institution}/gate/scan`}>
          <Button block size="large" type="primary" icon={<CameraOutlined />} style={{ height: 68, fontSize: 16 }}>
            Scan gate pass
          </Button>
        </Link>
        <Link to={`/i/${institution}/gate/lookup`}>
          <Button block size="large" icon={<SearchOutlined />} style={{ height: 68, fontSize: 16 }}>
            Manual lookup
          </Button>
        </Link>
      </div>
      {passes.data && passes.data.active > 0 && (
        <Card size="small" style={{ background: '#0f1a2b', borderColor: '#1e2a3d', textAlign: 'left' }}>
          <Typography.Text style={{ color: '#8fa3c0', fontSize: 12 }}>
            {passes.data.active} pass{passes.data.active === 1 ? '' : 'es'} currently valid
          </Typography.Text>
        </Card>
      )}
    </Space>
  );
}

/* =========== Scan =========== */

export function KioskScanPage() {
  const { institution = '' } = useParams<{ institution: string }>();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [result, setResult] = useState<GatePassRow | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const use = useMutation({
    mutationFn: (id: string) => productApi.useGatePass(institution, id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['gate-passes', institution] });
      void message.success('Pass used');
      setResult(null); setCode('');
      navigate(`/i/${institution}/gate`);
    },
    onError: (e) => void message.error(extractErrMsg(e) ?? 'Failed'),
  });

  async function verify() {
    if (!code.trim()) return;
    setBusy(true); setErr(null); setResult(null);
    try {
      const r = await productApi.lookupGatePass(institution, code.trim());
      setResult(r);
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) {
        setErr('No pass found with that code.');
      } else {
        setErr(extractErrMsg(e) ?? 'Lookup failed');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Typography.Title level={3} style={{ color: '#e6ebf3', margin: 0 }}>Scan gate pass</Typography.Title>
      <Card size="small" style={{ background: '#0f1a2b', borderColor: '#1e2a3d' }}>
        <Space size={8}>
          <Input size="large" value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            onPressEnter={verify}
            placeholder="GP-2026-00042"
            style={{
              width: 340, fontSize: 20, letterSpacing: 2,
              fontFamily: "'JetBrains Mono', ui-monospace, monospace",
              background: '#0b1420', color: '#e6ebf3', borderColor: '#1e2a3d',
            }}
            autoFocus
          />
          <Button type="primary" size="large" icon={<SearchOutlined />}
            loading={busy} onClick={verify} disabled={!code.trim()}>
            Verify
          </Button>
        </Space>
        <div style={{ marginTop: 8, fontSize: 11.5, color: '#8fa3c0' }}>
          Camera QR scanner arrives with the device build. For now, paste or type the pass code above.
        </div>
      </Card>

      {err && (
        <Alert type="error" showIcon message={err}
          style={{ background: '#2b1a10', borderColor: '#7a3421', color: '#f4a3a0' }} />
      )}

      {result && <PassResultCard pass={result} onUse={() => use.mutate(result.id)} />}
    </Space>
  );
}

function PassResultCard({ pass, onUse }: { pass: GatePassRow; onUse: () => void }) {
  const valid = pass.status === 'valid';
  const tone = valid ? '#1c6442' : pass.status === 'expired' ? '#a24040' : '#a24040';
  return (
    <Card size="small" style={{
      background: valid ? '#0f2a1a' : '#2b1a10',
      borderColor: valid ? '#1c5c3a' : '#7a3421',
    }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        {valid
          ? <CheckCircleOutlined style={{ fontSize: 44, color: '#7ee2b1' }} />
          : <CloseCircleOutlined style={{ fontSize: 44, color: '#f4a3a0' }} />}
        <div style={{ flex: 1 }}>
          <Typography.Title level={4} style={{ margin: 0, color: '#e6ebf3' }}>{pass.student_name}</Typography.Title>
          <div style={{ color: '#8fa3c0', fontSize: 13 }}>
            {pass.grade}{pass.section ? `-${pass.section}` : ''} · <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>{pass.admission_number}</span>
          </div>
        </div>
        <Tag color={valid ? 'success' : pass.status === 'used' ? 'default' : 'error'}
          style={{ margin: 0, fontSize: 14, padding: '4px 12px' }}>
          {pass.status.toUpperCase()}
        </Tag>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginTop: 14, fontSize: 13, color: '#e6ebf3' }}>
        <KVDark k="Pass code" v={pass.code} mono />
        <KVDark k="Reason" v={pass.reason} />
        <KVDark k="Valid until" v={new Date(pass.valid_until).toLocaleString()} />
        {pass.used_at && <KVDark k="Used at" v={new Date(pass.used_at).toLocaleString()} />}
        {pass.issued_by && <KVDark k="Issued by" v={pass.issued_by} />}
      </div>
      {valid && (
        <Popconfirm title="Mark this pass as used?" onConfirm={onUse}>
          <Button block type="primary" size="large" style={{ marginTop: 14, height: 56, fontSize: 16, background: tone, borderColor: tone }}>
            Let through · Mark used
          </Button>
        </Popconfirm>
      )}
      {!valid && (
        <Alert type="warning" showIcon
          message={
            pass.status === 'used' ? 'This pass has already been used — do not let them through again.'
            : pass.status === 'expired' ? 'This pass expired — refer to admin.'
            : 'This pass was revoked — refer to admin.'
          }
          style={{ marginTop: 14, background: '#2b1a10', borderColor: '#7a3421', color: '#f4a3a0' }}
        />
      )}
    </Card>
  );
}

function KVDark({ k, v, mono }: { k: string; v: string; mono?: boolean }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: '#8fa3c0', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>{k}</div>
      <div style={{ fontSize: 14, fontWeight: 500, fontFamily: mono ? "'JetBrains Mono', ui-monospace, monospace" : undefined }}>{v}</div>
    </div>
  );
}

/* =========== Lookup =========== */

export function KioskLookupPage() {
  const { institution = '' } = useParams<{ institution: string }>();
  const [search, setSearch] = useState('');
  const q: StudentListQuery = { q: search || undefined };
  const students = useQuery({
    queryKey: ['students', institution, q],
    queryFn: () => productApi.listStudents(institution, q),
    enabled: !!institution && search.length >= 2,
  });
  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Typography.Title level={3} style={{ color: '#e6ebf3', margin: 0 }}>Manual lookup</Typography.Title>
      <Card size="small" style={{ background: '#0f1a2b', borderColor: '#1e2a3d' }}>
        <Input size="large" value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Type student name or admission number"
          prefix={<SearchOutlined style={{ color: '#8fa3c0' }} />}
          style={{ background: '#0b1420', color: '#e6ebf3', borderColor: '#1e2a3d' }}
          autoFocus
        />
        <div style={{ marginTop: 8, fontSize: 11.5, color: '#8fa3c0' }}>
          Use this only when a QR is broken or missing. Match the student's photo before letting them through.
        </div>
      </Card>
      {search.length >= 2 && (
        <Card size="small" style={{ background: '#0f1a2b', borderColor: '#1e2a3d' }}>
          {students.isLoading
            ? <Skeleton active paragraph={{ rows: 3 }} />
            : (students.data?.rows.length ?? 0) === 0
              ? <Empty description={<span style={{ color: '#8fa3c0' }}>No matches.</span>} style={{ padding: 20 }} />
              : (
                <Space direction="vertical" size={6} style={{ width: '100%' }}>
                  {(students.data?.rows ?? []).slice(0, 20).map((s) => (
                    <div key={s.id} style={{
                      background: '#0b1420', border: '1px solid #1e2a3d', borderRadius: 6,
                      padding: '10px 12px', display: 'flex', gap: 12, alignItems: 'center',
                    }}>
                      <div style={{
                        width: 40, height: 40, borderRadius: '50%',
                        background: '#1e2a3d', color: '#7ab6ff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontWeight: 700,
                      }}>{s.name.charAt(0).toUpperCase()}</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: 14, color: '#e6ebf3' }}>{s.name}</div>
                        <div style={{ fontSize: 11.5, color: '#8fa3c0' }}>
                          <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>{s.admission_number}</span> · {s.grade}{s.section ? `-${s.section}` : ''}
                        </div>
                      </div>
                    </div>
                  ))}
                </Space>
              )}
        </Card>
      )}
      <Alert type="info" showIcon
        message="No pass on file? Refer them to admin."
        description="Issuing gate passes stays with the front desk — the kiosk only verifies."
        style={{ background: '#0f1a2b', borderColor: '#1e2a3d', color: '#8fa3c0' }} />
    </Space>
  );
}

/* =========== Vehicles =========== */

export function KioskVehiclesPage() {
  const { institution = '' } = useParams<{ institution: string }>();
  const qc = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const q = useQuery({
    queryKey: ['visitors', institution, today],
    queryFn: () => productApi.listVisitors(institution, today),
    enabled: !!institution,
  });
  const checkOut = useMutation({
    mutationFn: (id: string) => productApi.checkOutVisitor(institution, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['visitors', institution] }),
  });
  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'baseline' }}>
        <Typography.Title level={3} style={{ color: '#e6ebf3', margin: 0, flex: 1 }}>Vehicle & visitor log</Typography.Title>
        <Button type="primary" onClick={() => setAddOpen(true)}>Log vehicle / visitor</Button>
      </div>
      <Alert type="info" showIcon
        message="Vehicles use the visitor log for now"
        description="A dedicated vehicle table (plate + purpose + driver + exit time) ships when the fleet integration lands. Log the plate under Badge no. for now."
        style={{ background: '#0f1a2b', borderColor: '#1e2a3d', color: '#8fa3c0' }} />
      <Card size="small" style={{ background: '#0f1a2b', borderColor: '#1e2a3d' }}>
        {q.isLoading || !q.data ? <Skeleton active paragraph={{ rows: 3 }} />
          : q.data.rows.length === 0
            ? <Empty description={<span style={{ color: '#8fa3c0' }}>Nobody logged today.</span>} style={{ padding: 20 }} />
            : (
              <Space direction="vertical" size={6} style={{ width: '100%' }}>
                {q.data.rows.map((v) => (
                  <div key={v.id} style={{
                    background: '#0b1420', border: '1px solid #1e2a3d', borderRadius: 6,
                    padding: '10px 12px', display: 'flex', gap: 10, alignItems: 'center',
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 13.5, color: '#e6ebf3' }}>{v.name}</div>
                      <div style={{ fontSize: 11.5, color: '#8fa3c0' }}>
                        <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>{v.phone}</span>
                        {v.badge_no && ` · badge/plate ${v.badge_no}`}
                        {' · '}{v.purpose.replace(/_/g, ' ')}
                        {v.meeting_with && ` · to see ${v.meeting_with}`}
                      </div>
                    </div>
                    {v.out_at
                      ? <Tag>out at {new Date(v.out_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</Tag>
                      : <Button size="small" onClick={() => checkOut.mutate(v.id)}>Check out</Button>}
                  </div>
                ))}
              </Space>
            )}
      </Card>
      <VehicleAddModal open={addOpen} institution={institution}
        onClose={() => setAddOpen(false)}
        onDone={() => qc.invalidateQueries({ queryKey: ['visitors', institution] })} />
    </Space>
  );
}

function VehicleAddModal({
  open, institution, onClose, onDone,
}: { open: boolean; institution: string; onClose: () => void; onDone: () => void }) {
  const [form] = Form.useForm<CheckInVisitorRequest>();
  const m = useMutation({
    mutationFn: (input: CheckInVisitorRequest) => productApi.checkInVisitor(institution, input),
    onSuccess: () => { form.resetFields(); onDone(); onClose(); void message.success('Logged'); },
  });
  const err = extractErrMsg(m.error);
  return (
    <Modal open={open} title="Log vehicle / visitor"
      onCancel={() => { m.reset(); onClose(); }}
      okText="Log" confirmLoading={m.isPending} onOk={() => form.submit()} destroyOnClose>
      <Form form={form} layout="vertical"
        initialValues={{ purpose: 'vendor' as CheckInVisitorRequest['purpose'] }}
        onFinish={(v) => m.mutate({
          name: v.name.trim(), phone: v.phone.trim(),
          purpose: v.purpose,
          meeting_with: v.meeting_with?.trim() || undefined,
          badge_no: v.badge_no?.trim() || undefined,
          note: v.note?.trim() || undefined,
        })}>
        <Form.Item name="name" label="Driver / visitor name" rules={[{ required: true, max: 160 }]}><Input /></Form.Item>
        <Form.Item name="phone" label="Phone" rules={[{ required: true }]}>
          <Input placeholder="+91 ..." style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }} />
        </Form.Item>
        <Form.Item name="badge_no" label="Vehicle plate / badge">
          <Input placeholder="e.g. KA-01-AB-1234" style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }} />
        </Form.Item>
        <Form.Item name="purpose" label="Purpose"><Input placeholder="parent_meeting / vendor / …" /></Form.Item>
        <Form.Item name="meeting_with" label="To see"><Input /></Form.Item>
        <Form.Item name="note" label="Note"><Input.TextArea rows={2} /></Form.Item>
        {err && <Alert type="error" showIcon message={err} />}
      </Form>
    </Modal>
  );
}

/* =========== Shift =========== */

export function KioskShiftPage() {
  const [shift, setShift] = useState<ShiftState | null>(readShift());
  const [name, setName] = useState('');
  useEffect(() => {
    const onChange = () => setShift(readShift());
    window.addEventListener('velora:gate-shift', onChange);
    return () => window.removeEventListener('velora:gate-shift', onChange);
  }, []);
  const label = useMemo(() => {
    if (!shift) return null;
    const dur = Math.floor((Date.now() - Date.parse(shift.startedAt)) / 60_000);
    return `${dur} min on duty`;
  }, [shift]);
  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Typography.Title level={3} style={{ color: '#e6ebf3', margin: 0 }}>Shift</Typography.Title>
      <Alert type="info" showIcon
        message="Signed in here on this device only"
        description="Central shift roster (with handovers audited on server) ships with the HR shifts module later."
        style={{ background: '#0f1a2b', borderColor: '#1e2a3d', color: '#8fa3c0' }} />
      <Card size="small" style={{ background: '#0f1a2b', borderColor: '#1e2a3d' }}>
        {shift ? (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <div style={{
                width: 44, height: 44, borderRadius: '50%',
                background: '#1c5c3a', color: '#7ee2b1',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 700,
              }}>{shift.name.charAt(0).toUpperCase()}</div>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#e6ebf3' }}>{shift.name}</div>
                <div style={{ fontSize: 12, color: '#8fa3c0' }}>
                  Started {new Date(shift.startedAt).toLocaleString()} · {label}
                </div>
              </div>
            </div>
            <Popconfirm title="Sign out of this shift?" onConfirm={() => { signOutShift(); setShift(null); }}>
              <Button danger icon={<LogoutOutlined />} block>Sign out</Button>
            </Popconfirm>
          </Space>
        ) : (
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            <Typography.Text style={{ color: '#8fa3c0' }}>Enter your name to sign in.</Typography.Text>
            <Input size="large" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="Guard name"
              style={{ background: '#0b1420', color: '#e6ebf3', borderColor: '#1e2a3d' }} />
            <Button block type="primary" size="large" icon={<LoginOutlined />}
              disabled={!name.trim()}
              onClick={() => setShift(signInShift(name.trim()))}>
              Sign in
            </Button>
          </Space>
        )}
      </Card>
      {shift && (
        <Card size="small" title={<span style={{ color: '#e6ebf3' }}>Handover</span>}
          style={{ background: '#0f1a2b', borderColor: '#1e2a3d' }}>
          <Typography.Text style={{ color: '#8fa3c0', fontSize: 12.5 }}>
            To hand over, sign out and let the incoming guard sign in with their name.
            This keeps the "on duty" indicator honest at the top of every kiosk page.
          </Typography.Text>
          <Button block style={{ marginTop: 12 }} icon={<SwapOutlined />}
            onClick={() => { signOutShift(); setShift(null); setName(''); }}>
            End my shift · pass to next
          </Button>
        </Card>
      )}
    </Space>
  );
}
